import { useEffect, useState } from "react";
import { API_BASE } from "../../utils/api";
import { getMyAccountId } from "../../utils/config";

export default function OrderDetailModal({ open, onClose, order, onDuplicate }) {
  const [items, setItems] = useState([]);
  const [previewImg, setPreviewImg] = useState(null);
  const MYACCOUNT_ID = getMyAccountId();
  // 🔹 Cargar items cuando se abre el modal
  useEffect(() => {
    if (!open || !order) return;

    const fetchItems = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/orders-ultra/api/ws/listItems_ecommerce?order_id=${order.orderId}`
        );
        const data = await response.json();
        setItems(data);
      } catch (error) {
        console.error("Error cargando items:", error);
      }
    };

    fetchItems();
  }, [open, order]);

  // Bloquear scroll cuando modal está abierto
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [open]);

  if (!open || !order) return null;

  return (
    <>
      {/* OVERLAY */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white w-[95%] md:w-[850px] rounded-xl shadow-xl p-6 relative">

          {/* BOTÓN DUPLICAR */}
          <button
            onClick={() => onDuplicate(order, items)}
            className="absolute top-3 left-3 bg-brand-primary text-white px-3 py-1 rounded-md text-sm font-semibold shadow hover:brightness-90"
          >
            Duplicar Orden
          </button>

          {/* BOTÓN CERRAR */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-black text-xl"
          >
            ✕
          </button>

          <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">
            Detalle de la Orden Nro {order.orderNumber}
          </h2>

          {/* CONTENEDOR */}
          <div className="flex flex-col max-h-[600px] border rounded-lg overflow-hidden">

            {/* CABECERA */}
            <div className="p-4 bg-gray-50 border-b">
              <div className="grid grid-cols-2 gap-4 text-gray-700 text-sm">
                <p><strong>Fecha:</strong>
                  {new Date(order.orderDate).toLocaleDateString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric"
                  })}
                </p>
                <p><strong>Estado:</strong> {order.crossId === null ? "PENDIENTE" : "FACTURADO"}</p>
                <p><strong>Usuario:</strong> {order.employeeId}</p>
                <p><strong>Notas:</strong> {order.notes}</p>
              </div>
            </div>

            {/* TABLA */}
            <div className="overflow-y-auto flex-1">
              {items.length === 0 ? (
                <p className="p-4 text-gray-500 text-sm">Cargando items...</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 border-b sticky top-0 z-10">
                    <tr className="text-left text-gray-700 font-semibold">
                      <th className="p-3">Imagen</th>
                      <th className="p-2">Código</th>
                      <th className="p-2">Descripción</th>
                      <th className="text-right p-2">Cant</th>
                      <th className="text-right p-2">Precio</th>

                      {/* ➕ NUEVA COLUMNA IVA */}
                      <th className="text-right p-2">IVA</th>

                      <th className="text-right p-2">Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {items.map((i) => (
                      <tr key={i.orderLineId} className="border-b hover:bg-gray-100 transition">
                        <td className="p-3">
                          <img
                            src={`https://www.ultra-erp.pro/products/${MYACCOUNT_ID}/${i.productId}.jpg`}
                            className="w-14 h-14 rounded cursor-pointer hover:scale-110 transition"
                            onClick={() =>
                              setPreviewImg(`https://www.ultra-erp.pro/products/${MYACCOUNT_ID}/${i.productId}.jpg`)
                            }
                            onError={(e) => (e.target.src = "https://via.placeholder.com/50")}
                          />
                        </td>

                        <td className="p-2">{i.productId}</td>
                        <td className="p-2">{i.productName}</td>
                        <td className="p-2 text-right">{i.quantity}</td>
                        <td className="p-2 text-right">{i.unitPrice?.toFixed(4)}</td>

                        {/* ➕ COLUMNA IVA POR ITEM */}
                        <td className="p-2 text-right">
                          {i.taxValue > 0 ? "*" : ""}
                        </td>

                        <td className="p-2 text-right font-semibold">
                          {i.totalPrice?.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* FOOTER RESUMEN */}
            <div className="p-4 bg-gray-50 border-t">
              <div className="flex flex-col items-end gap-2 text-right w-full max-w-xs ml-auto">

                <div className="grid grid-cols-2 gap-4 w-full">
                  <span>Subtotal:</span>
                  <span className="font-bold">{order.taxable?.toFixed(2)}</span>
                </div>

                {order.totalExento > 0 && (
                  <div className="grid grid-cols-2 gap-4 w-full">
                    <span>Subtotal 0%:</span>
                    <span className="font-bold">{order.totalExento?.toFixed(2)}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 w-full">
                  <span>IVA:</span>
                  <span className="font-bold">{order.taxTotal?.toFixed(2)}</span>
                </div>

                <hr className="w-full my-2" />

                <div className="grid grid-cols-2 gap-4 w-full">
                  <span className="text-lg font-bold">Total:</span>
                  <span className="text-lg font-bold">${order.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* IMAGEN AMPLIADA */}
      {previewImg && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[999]"
          onClick={() => setPreviewImg(null)}
        >
          <img
            src={previewImg}
            className="max-w-[60%] max-h-[60%] rounded-lg shadow-xl border-2 border-white"
          />
        </div>
      )}
    </>
  );
}
